<template>
  <div class="recycle-management">
    <h2>二奢回收管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>回收表单列表</span>
        </div>
      </template>
      <div class="recycle-list-content">
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入用户昵称、手机号码、商品描述"
            prefix-icon="Search"
            clearable
            class="search-input"
          >
            <template #append>
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon>
                搜索
              </el-button>
            </template>
          </el-input>
          <div class="search-filters">
            <el-select v-model="searchForm.status" placeholder="状态" clearable>
              <el-option label="待审核" value="pending" />
              <el-option label="已拒绝" value="rejected" />
              <el-option label="可回收" value="recyclable" />
              <el-option label="已到店" value="arrived" />
            </el-select>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="recycleFormList"
          stripe
          style="width: 100%"
          class="recycle-table"
          :row-key="row => row.id"
        >
          <el-table-column label="用户信息" width="200">
            <template #default="scope">
              <div class="user-info">
                <div class="nickname">{{ scope.row.nickname || '未知' }}</div>
                <div class="phone">{{ scope.row.phone || '未知' }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="description" label="商品描述" width="250" show-overflow-tooltip />
          <el-table-column label="商品图片" width="120">
            <template #default="scope">
              <el-button type="text" @click="showProductImages(scope.row.images)">
                查看图片
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="提交时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column prop="status" label="当前状态" width="120">
            <template #default="scope">
              <el-tag :type="getStatusTagType(scope.row.status)">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="到店时间" width="200">
            <template #default="scope">
              {{ parseRecyclingTimeSlot(scope.row.recycling_time_slot) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="250" fixed="right">
            <template #default="scope">
              <!-- 待审核状态可以审核 -->
              <el-button
                v-if="scope.row.status === 'pending'"
                type="primary"
                size="small"
                @click="showAuditDialog(scope.row)"
              >
                <el-icon><Check /></el-icon>
                审核
              </el-button>
              <!-- 可回收状态可以标记为已到店 -->
              <el-button
                v-else-if="scope.row.status === 'recyclable'"
                type="success"
                size="small"
                @click="handleMarkAsArrived(scope.row)"
              >
                <el-icon><Location /></el-icon>
                标记已到店
              </el-button>
              <!-- 所有状态都可以查看详情 -->
              <el-button
                type="info"
                size="small"
                @click="showRecycleDetail(scope.row)"
              >
                <el-icon><View /></el-icon>
                详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 审核弹窗 -->
    <el-dialog
      v-model="auditDialogVisible"
      title="审核回收表单"
      width="50%"
    >
      <div v-if="currentRecycleForm" class="audit-dialog-content">
        <el-descriptions :column="1" border>
          <el-descriptions-item label="用户昵称">{{ currentRecycleForm.nickname }}</el-descriptions-item>
          <el-descriptions-item label="手机号码">{{ currentRecycleForm.phone }}</el-descriptions-item>
          <el-descriptions-item label="商品描述">{{ currentRecycleForm.description }}</el-descriptions-item>
          <el-descriptions-item label="当前状态">{{ getStatusText(currentRecycleForm.status) }}</el-descriptions-item>
          <el-descriptions-item label="提交时间">{{ formatDateTime(currentRecycleForm.created_at) }}</el-descriptions-item>
        </el-descriptions>
        
        <div class="audit-form">
          <el-form :model="auditForm" label-width="80px">
            <el-form-item label="审核结果" required>
              <el-radio-group v-model="auditForm.status">
                <el-radio label="rejected">拒绝</el-radio>
                <el-radio label="recyclable">可回收</el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="审核反馈" required v-if="auditForm.status">
              <el-input
                v-model="auditForm.feedback"
                type="textarea"
                :rows="4"
                placeholder="请输入审核反馈"
              />
            </el-form-item>
          </el-form>
        </div>
      </div>
      
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="auditDialogVisible = false">取消</el-button>
          <el-button
            type="primary" @click="handleAudit"
            :disabled="!auditForm.status || !auditForm.feedback"
          >
            提交审核
          </el-button>
        </div>
      </template>
    </el-dialog>
    
    <!-- 详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="回收表单详情"
      width="60%"
    >
      <div v-if="currentRecycleForm" class="detail-dialog-content">
          <el-descriptions :column="2" border>
            <el-descriptions-item label="用户昵称">{{ currentRecycleForm.nickname }}</el-descriptions-item>
            <el-descriptions-item label="手机号码">{{ currentRecycleForm.phone }}</el-descriptions-item>
            <el-descriptions-item label="状态">
              <el-tag :type="getStatusTagType(currentRecycleForm.status)">
                {{ getStatusText(currentRecycleForm.status) }}
              </el-tag>
            </el-descriptions-item>
            <el-descriptions-item label="提交时间" :span="2">{{ formatDateTime(currentRecycleForm.created_at) }}</el-descriptions-item>
            <el-descriptions-item label="商品描述" :span="2">{{ currentRecycleForm.description }}</el-descriptions-item>
            <el-descriptions-item label="到店时间" :span="2">{{ parseRecyclingTimeSlot(currentRecycleForm.recycling_time_slot) }}</el-descriptions-item>
            <el-descriptions-item label="审核反馈" :span="2">{{ currentRecycleForm.feedback || '无' }}</el-descriptions-item>
            <el-descriptions-item label="审核时间" :span="2">{{ currentRecycleForm.audited_at ? formatDateTime(currentRecycleForm.audited_at) : '未审核' }}</el-descriptions-item>
            <el-descriptions-item label="实际到店时间" :span="2">{{ currentRecycleForm.arrived_at ? formatDateTime(currentRecycleForm.arrived_at) : '未到店' }}</el-descriptions-item>
          </el-descriptions>
        
        <div class="images-section" v-if="currentRecycleForm.images && currentRecycleForm.images.length > 0">
          <h4>商品图片</h4>
          <div class="images-grid">
            <el-image
              v-for="(image, index) in currentRecycleForm.images"
              :key="index"
              :src="image"
              fit="cover"
              style="width: 150px; height: 150px; margin: 10px; cursor: pointer"
              :preview-src-list="currentRecycleForm.images"
            />
          </div>
        </div>
      </div>
    </el-dialog>
    
    <!-- 图片预览弹窗 -->
    <el-dialog
      v-model="imageDialogVisible"
      title="商品图片"
      width="80%"
    >
      <el-image
        v-for="(image, index) in previewImages"
        :key="index"
        :src="image"
        fit="contain"
        style="width: 100%; height: 500px; margin-bottom: 20px"
        :preview-src-list="previewImages"
      />
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, Check, View, Location } from '@element-plus/icons-vue';
import * as recycleApi from '../../api/recycleManagement';

export default {
  name: 'LuxuryRecycle',
  components: {
    Search,
    Check,
    View,
    Location
  },
  setup() {
    // 搜索表单
    const searchForm = reactive({
      keyword: '',
      status: ''
    });

    // 加载状态
    const loading = ref(false);

    // 回收表单列表数据
    const recycleFormList = ref([]);

    // 分页数据
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });
    
    // 审核相关
    const auditDialogVisible = ref(false);
    const currentRecycleForm = ref(null);
    const auditForm = reactive({
      status: '',
      feedback: ''
    });
    
    // 详情相关
    const detailDialogVisible = ref(false);
    
    // 图片预览相关
    const imageDialogVisible = ref(false);
    const previewImages = ref([]);

    // 获取状态文本
    const getStatusText = (status) => {
      const statusMap = {
        pending: '待审核',
        rejected: '已拒绝',
        recyclable: '可回收',
        arrived: '已到店'
      };
      return statusMap[status] || status;
    };
    
    // 获取状态标签类型
    const getStatusTagType = (status) => {
      const typeMap = {
        pending: 'warning',
        rejected: 'danger',
        recyclable: 'success',
        arrived: 'info'
      };
      return typeMap[status] || 'info';
    };
    
    // 解析到店时间JSON
    const parseRecyclingTimeSlot = (timeSlot) => {
      if (!timeSlot) return '未选择';
      try {
        // 如果已经是字符串，尝试解析为JSON对象
        const slotData = typeof timeSlot === 'string' ? JSON.parse(timeSlot) : timeSlot;
        // 拼接日期和时间段
        if (slotData.date) {
          if (slotData.start_time && slotData.end_time) {
            return `${slotData.date} ${slotData.start_time}-${slotData.end_time}`;
          }
          return slotData.date;
        }
        return timeSlot;
      } catch (error) {
        // 如果解析失败，返回原始数据
        return timeSlot;
      }
    };
    
    // 格式化日期时间为 YMD HIS 格式
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-';
      try {
        const date = new Date(dateTime);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return dateTime;
      }
    };

    // 加载回收表单列表
    const loadRecycleForms = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize,
          search: searchForm.keyword,
          status: searchForm.status
        };
        const response = await recycleApi.fetchRecycleFormList(params);
        if (response.code === 200) {
          recycleFormList.value = response.data;
          pagination.total = response.total;
        } else {
          ElMessage.error('获取回收表单列表失败');
        }
      } catch (error) {
        ElMessage.error('获取回收表单列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 搜索
    const handleSearch = () => {
      pagination.currentPage = 1;
      loadRecycleForms();
    };
    
    // 显示审核弹窗
    const showAuditDialog = (row) => {
      currentRecycleForm.value = row;
      auditForm.status = '';
      auditForm.feedback = '';
      auditDialogVisible.value = true;
    };
    
    // 显示详情弹窗
    const showRecycleDetail = async (row) => {
      try {
        const response = await recycleApi.fetchRecycleFormDetail(row.id);
        if (response.code === 200) {
          currentRecycleForm.value = response.data;
          detailDialogVisible.value = true;
        } else {
          ElMessage.error('获取详情失败');
        }
      } catch (error) {
        ElMessage.error('获取详情失败');
        console.error(error);
      }
    };
    
    // 显示商品图片
    const showProductImages = (images) => {
      if (images && Array.isArray(images)) {
        previewImages.value = images;
        imageDialogVisible.value = true;
      } else if (images) {
        previewImages.value = [images];
        imageDialogVisible.value = true;
      }
    };
    
    // 提交审核
    const handleAudit = async () => {
      if (!currentRecycleForm.value) return;
      
      try {
        const response = await recycleApi.auditRecycleForm(currentRecycleForm.value.id, {
          status: auditForm.status,
          feedback: auditForm.feedback
        });
        if (response.code === 200) {
          ElMessage.success('审核成功');
          auditDialogVisible.value = false;
          loadRecycleForms();
        } else {
          ElMessage.error('审核失败');
        }
      } catch (error) {
        ElMessage.error('审核失败');
        console.error(error);
      }
    };
    
    // 标记为已到店
    const handleMarkAsArrived = async (row) => {
      try {
        const response = await recycleApi.markRecycleFormAsArrived(row.id);
        if (response.code === 200) {
          ElMessage.success('标记已到店成功');
          loadRecycleForms();
        } else {
          ElMessage.error('标记已到店失败');
        }
      } catch (error) {
        ElMessage.error('标记已到店失败');
        console.error(error);
      }
    };

    // 分页大小变化
    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      loadRecycleForms();
    };

    // 当前页变化
    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      loadRecycleForms();
    };

    // 组件挂载时加载数据
    onMounted(() => {
      loadRecycleForms();
    });

    return {
      searchForm,
      loading,
      recycleFormList,
      pagination,
      auditDialogVisible,
      currentRecycleForm,
      auditForm,
      detailDialogVisible,
      imageDialogVisible,
      previewImages,
      getStatusText,
      getStatusTagType,
      parseRecyclingTimeSlot,
      formatDateTime,
      handleSearch,
      showAuditDialog,
      showRecycleDetail,
      showProductImages,
      handleAudit,
      handleMarkAsArrived,
      handleSizeChange,
      handleCurrentChange
    };
  }
};
</script>

<style scoped>
.recycle-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-area {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.search-input {
  width: 400px;
}

.search-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.recycle-list-content {
  margin-top: 20px;
}

.recycle-table {
  margin-bottom: 20px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 审核弹窗样式 */
.audit-dialog-content {
  padding: 10px;
}

.audit-form {
  margin-top: 20px;
}

/* 详情弹窗样式 */
.detail-dialog-content {
  padding: 10px;
}

.images-section {
  margin-top: 20px;
}

.images-section h4 {
  margin-bottom: 15px;
  font-size: 16px;
  color: #333;
}

.images-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
</style>